// FILE: FocusAttentionIndicator.tsx
// Purpose: Shows attention waiting in projects other than the focused one.
// Exports: FocusAttentionIndicator

import { useNavigate } from "@tanstack/react-router";

import type { CrossProjectAttention } from "../Sidebar.focus.logic";
import { ATTENTION_COUNT_PILL_CLASS } from "../Sidebar.logic";
import { useFocusedProject } from "~/focusMode";
import { cn } from "~/lib/utils";
import { ProjectAvatar } from "../ProjectAvatar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

const ATTENTION_KIND_LABEL = {
  approval: "Aprobación pendiente",
  input: "Esperando input",
  plan: "Plan listo",
  completed: "Completado",
} as const;

type FocusAttentionIndicatorProps = {
  attention: CrossProjectAttention;
  className?: string;
};

export function FocusAttentionIndicator(props: FocusAttentionIndicatorProps) {
  const navigate = useNavigate();
  const { focusedProject, setFocusedProject } = useFocusedProject();
  const { attention, className } = props;

  if (!focusedProject || attention.level === null || attention.total < 1) return null;

  const isUrgent = attention.level === "urgent";
  const openThread = (
    projectId: (typeof attention.byProject)[number]["project"]["id"],
    threadId: string,
  ) => {
    setFocusedProject(projectId);
    void navigate({ to: "/$threadId", params: { threadId } });
  };
  const showAllProjects = () => {
    setFocusedProject(null);
    void navigate({ to: "/" });
  };

  return (
    <Popover>
      <PopoverTrigger
        aria-label={`${attention.total} hilos requieren atención en otros proyectos`}
        className={cn(
          "inline-flex shrink-0 items-center justify-center hover:bg-accent/60",
          ATTENTION_COUNT_PILL_CLASS,
          isUrgent ? "text-red-600 dark:text-red-300" : "text-amber-700 dark:text-amber-300",
          className,
        )}
      >
        {attention.total}
        <span className="sr-only">hilos requieren atención en otros proyectos</span>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="max-h-96 overflow-y-auto p-2">
          {attention.byProject.map(({ project, threads }) => (
            <div key={project.id} className="py-1">
              <div className="flex items-center gap-1.5 px-2 py-1 text-[13px] font-medium text-muted-foreground">
                <ProjectAvatar project={project} size="sm" />
                {project.name}
              </div>
              {threads.map(({ thread, kind }) => (
                <button
                  key={thread.id}
                  type="button"
                  className="flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent/60"
                  onClick={() => openThread(project.id, thread.id)}
                >
                  <span className="min-w-0 flex-1 truncate text-xs text-foreground">
                    {thread.title}
                  </span>
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                      kind === "completed"
                        ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                        : "bg-red-500/15 text-red-600 dark:text-red-300",
                    )}
                  >
                    {ATTENTION_KIND_LABEL[kind]}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="border-t border-border p-1.5">
          <button
            type="button"
            className="w-full rounded-md px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent/60 hover:text-foreground"
            onClick={showAllProjects}
          >
            Ver todos los proyectos
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
